import {createAsyncThunk} from '@reduxjs/toolkit'
import axios from 'axios'
import {
  zadProcurementDocumentAttachmentRemoveUrl,
  zadProcurementDocumentAttachmentsUrl,
  zadProcurementDocumentByIdUrl,
  zadProcurementDocumentConvertToPurchaseUrl,
  zadProcurementDocumentPrintUrl,
  zadProcurementDocumentQuoteUrl,
  zadProcurementDocumentStatusUrl,
  zadProcurementDocumentUpdateUrl,
  zadProcurementDocumentsUrl,
  zadProductsUrl,
  zadSuppliersUrl,
} from '../../api'
import {parseZadApiError} from '../zad/errors'
import {
  buildZadProcurementQuery,
  extractZadProcurementConvertResult,
  extractZadProcurementDocument,
  extractZadProcurementList,
  extractZadProcurementPrintDto,
  sanitizeZadProcurementWritePayload,
} from '../zad/procurementHelpers'
import {buildZadListQuery} from '../zad/query'
import {
  ConvertZadProcurementToPurchaseRequest,
  CreateZadProcurementRequest,
  FetchZadProcurementByIdRequest,
  FetchZadProcurementDocumentsRequest,
  FetchZadProcurementPickerRequest,
  FetchZadProcurementPrintRequest,
  UpdateZadProcurementRequest,
  ZadProcurementAttachmentRemoveRequest,
  ZadProcurementAttachmentRequest,
  ZadProcurementQuoteRequest,
  ZadProcurementStatusRequest,
} from './types'

export const fetchZadProcurementDocuments = createAsyncThunk(
  'zadProcurement/fetch',
  async (payload: FetchZadProcurementDocumentsRequest, {rejectWithValue}) => {
    try {
      const res = await axios.get(
        `${zadProcurementDocumentsUrl}?${buildZadProcurementQuery(payload)}`
      )
      const list = extractZadProcurementList(res.data || {})
      return {success: true as const, items: list.items, total: list.total, totalPages: list.totalPages}
    } catch (error) {
      return rejectWithValue(parseZadApiError(error))
    }
  }
)

export const fetchZadProcurementById = createAsyncThunk(
  'zadProcurement/fetchById',
  async (payload: FetchZadProcurementByIdRequest, {rejectWithValue}) => {
    try {
      const res = await axios.get(zadProcurementDocumentByIdUrl(payload.id))
      const document = extractZadProcurementDocument(res.data || {})
      if (!document) {
        return rejectWithValue({success: false as const, error: 'Procurement document not found'})
      }
      return {success: true as const, document}
    } catch (error) {
      return rejectWithValue(parseZadApiError(error))
    }
  }
)

export const createZadProcurementDocument = createAsyncThunk(
  'zadProcurement/create',
  async (payload: CreateZadProcurementRequest, {rejectWithValue}) => {
    try {
      const res = await axios.post(zadProcurementDocumentsUrl, payload)
      const document = extractZadProcurementDocument(res.data || {})
      if (!document) {
        return rejectWithValue({success: false as const, error: 'Procurement document not created'})
      }
      return {success: true as const, document}
    } catch (error) {
      return rejectWithValue(parseZadApiError(error))
    }
  }
)

export const updateZadProcurementDocument = createAsyncThunk(
  'zadProcurement/update',
  async (payload: UpdateZadProcurementRequest, {rejectWithValue}) => {
    try {
      const {id, ...body} = payload
      const res = await axios.post(zadProcurementDocumentUpdateUrl(id), body)
      const document = extractZadProcurementDocument(res.data || {})
      if (!document) {
        return rejectWithValue({success: false as const, error: 'Procurement document not updated'})
      }
      return {success: true as const, document}
    } catch (error) {
      return rejectWithValue(parseZadApiError(error))
    }
  }
)

export const updateZadProcurementStatus = createAsyncThunk(
  'zadProcurement/status',
  async (payload: ZadProcurementStatusRequest, {rejectWithValue}) => {
    try {
      const res = await axios.post(zadProcurementDocumentStatusUrl(payload.id), payload.body)
      const document = extractZadProcurementDocument(res.data || {})
      if (!document) {
        return rejectWithValue({success: false as const, error: 'Status update failed'})
      }
      return {success: true as const, document}
    } catch (error) {
      return rejectWithValue(parseZadApiError(error))
    }
  }
)

export const submitZadProcurementQuote = createAsyncThunk(
  'zadProcurement/quote',
  async (payload: ZadProcurementQuoteRequest, {rejectWithValue}) => {
    try {
      const res = await axios.post(zadProcurementDocumentQuoteUrl(payload.id), payload.body)
      const document = extractZadProcurementDocument(res.data || {})
      if (!document) {
        return rejectWithValue({success: false as const, error: 'Quote update failed'})
      }
      return {success: true as const, document}
    } catch (error) {
      return rejectWithValue(parseZadApiError(error))
    }
  }
)

export const uploadZadProcurementAttachment = createAsyncThunk(
  'zadProcurement/uploadAttachment',
  async (payload: ZadProcurementAttachmentRequest, {rejectWithValue}) => {
    try {
      const data = new FormData()
      data.append('file', payload.file)
      const res = await axios.post(zadProcurementDocumentAttachmentsUrl(payload.id), data, {
        headers: {'Content-Type': 'multipart/form-data'},
      })
      const document = extractZadProcurementDocument(res.data || {})
      if (!document) {
        return rejectWithValue({success: false as const, error: 'Attachment upload failed'})
      }
      return {success: true as const, document}
    } catch (error) {
      return rejectWithValue(parseZadApiError(error))
    }
  }
)

export const removeZadProcurementAttachment = createAsyncThunk(
  'zadProcurement/removeAttachment',
  async (payload: ZadProcurementAttachmentRemoveRequest, {rejectWithValue}) => {
    try {
      const res = await axios.post(
        zadProcurementDocumentAttachmentRemoveUrl(payload.id, payload.attachmentId)
      )
      const document = extractZadProcurementDocument(res.data || {})
      if (!document) {
        return rejectWithValue({success: false as const, error: 'Attachment remove failed'})
      }
      return {success: true as const, document}
    } catch (error) {
      return rejectWithValue(parseZadApiError(error))
    }
  }
)

export const fetchZadProcurementPrint = createAsyncThunk(
  'zadProcurement/print',
  async (payload: FetchZadProcurementPrintRequest, {rejectWithValue}) => {
    try {
      const res = await axios.get(zadProcurementDocumentPrintUrl(payload.id))
      const print = extractZadProcurementPrintDto(res.data || {})
      if (!print?.document) {
        return rejectWithValue({success: false as const, error: 'Print data not found'})
      }
      return {success: true as const, document: print.document, issuer: print.issuer}
    } catch (error) {
      return rejectWithValue(parseZadApiError(error))
    }
  }
)

export const convertZadProcurementToPurchase = createAsyncThunk(
  'zadProcurement/convertToPurchase',
  async (payload: ConvertZadProcurementToPurchaseRequest, {rejectWithValue}) => {
    try {
      const res = await axios.post(zadProcurementDocumentConvertToPurchaseUrl(payload.id), {})
      const result = extractZadProcurementConvertResult(res.data || {})
      if (!result.document && !result.conversion?.isConverted) {
        return rejectWithValue({success: false as const, error: 'Conversion failed'})
      }
      return {
        success: true as const,
        document: result.document,
        conversion: result.conversion,
      }
    } catch (error) {
      return rejectWithValue(parseZadApiError(error))
    }
  }
)

export const fetchZadProcurementPickerSuppliers = createAsyncThunk(
  'zadProcurement/pickerSuppliers',
  async (payload: FetchZadProcurementPickerRequest | void, {rejectWithValue}) => {
    try {
      const res = await axios.get(
        `${zadSuppliersUrl}?${buildZadListQuery({
          pageNumber: 1,
          pageCount: 100,
          sortField: 'name',
          sortOrder: 'asc',
          isActive: 'true',
          search: payload?.search || '',
        })}`
      )
      const {suppliers} = res.data || {}
      return {
        success: true as const,
        suppliers: Array.isArray(suppliers) ? suppliers : [],
      }
    } catch (error) {
      return rejectWithValue(parseZadApiError(error))
    }
  }
)

export const fetchZadProcurementPickerProducts = createAsyncThunk(
  'zadProcurement/pickerProducts',
  async (payload: FetchZadProcurementPickerRequest | void, {rejectWithValue}) => {
    try {
      const res = await axios.get(
        `${zadProductsUrl}?${buildZadListQuery({
          pageNumber: 1,
          pageCount: 50,
          sortField: 'name',
          sortOrder: 'asc',
          isActive: 'true',
          search: payload?.search || '',
        })}`
      )
      const {products} = res.data || {}
      return {
        success: true as const,
        products: Array.isArray(products) ? products : [],
      }
    } catch (error) {
      return rejectWithValue(parseZadApiError(error))
    }
  }
)

export {sanitizeZadProcurementWritePayload}
